/**
 * Mega menu for the AG header.
 *
 * The trigger stays a real link to the category page, so it works with
 * JavaScript off, for crawlers and for anyone who just wants the landing
 * page. On a pointer device the panel opens on hover or focus; on a touch
 * device the first tap opens it and the second follows the link.
 */

const CLOSE_DELAY = 140;

class AgMegaMenu {
  /** @type {HTMLElement | null} */
  #open = null;
  /** @type {number | undefined} */
  #timer;

  constructor() {
    this.triggers = /** @type {HTMLAnchorElement[]} */ (
      Array.from(document.querySelectorAll('[data-ag-mega-trigger]'))
    );
    if (!this.triggers.length) return;

    for (const trigger of this.triggers) {
      const panel = this.#panelFor(trigger);
      if (!panel) continue;

      trigger.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'touch') return;
        this.#show(trigger, panel);
      });
      trigger.addEventListener('pointerleave', (event) => {
        if (event.pointerType === 'touch') return;
        this.#scheduleHide();
      });
      trigger.addEventListener('focus', () => this.#show(trigger, panel));
      trigger.addEventListener('click', (event) => {
        // Coarse pointers have no hover: open on the first tap, navigate on the second.
        if (!window.matchMedia('(hover: none)').matches) return;
        if (panel.hidden) {
          event.preventDefault();
          this.#show(trigger, panel);
        }
      });

      panel.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'touch') return;
        this.#show(trigger, panel);
      });
      panel.addEventListener('pointerleave', (event) => {
        if (event.pointerType === 'touch') return;
        this.#scheduleHide();
      });
    }

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !this.#open) return;
      const trigger = this.#triggerFor(this.#open);
      this.#hide();
      trigger?.focus();
    });

    // Focus or a tap outside the open panel closes it.
    document.addEventListener('focusin', (event) => this.#maybeCloseFrom(event.target));
    document.addEventListener('pointerdown', (event) => this.#maybeCloseFrom(event.target));
  }

  /** @param {EventTarget | null} target */
  #maybeCloseFrom(target) {
    if (!this.#open || !(target instanceof Node)) return;
    const trigger = this.#triggerFor(this.#open);
    if (this.#open.contains(target) || trigger?.contains(target)) return;
    this.#hide();
  }

  /** @param {HTMLElement} trigger */
  #panelFor(trigger) {
    const id = trigger.dataset.agMegaTrigger;
    return id ? document.getElementById(id) : null;
  }

  /** @param {HTMLElement} panel */
  #triggerFor(panel) {
    return this.triggers.find((t) => t.dataset.agMegaTrigger === panel.id) ?? null;
  }

  /**
   * @param {HTMLElement} trigger
   * @param {HTMLElement} panel
   */
  #show(trigger, panel) {
    clearTimeout(this.#timer);
    // Moving straight from one open panel to the next only swaps the content;
    // the panel itself does not drop in again (ag-design.css, mega motion).
    const switching = !!this.#open && this.#open !== panel;
    if (switching) this.#hide();
    if (panel.hidden) panel.toggleAttribute('data-ag-mega-switch', switching);
    panel.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');
    this.#open = panel;
  }

  #scheduleHide() {
    clearTimeout(this.#timer);
    this.#timer = window.setTimeout(() => this.#hide(), CLOSE_DELAY);
  }

  #hide() {
    clearTimeout(this.#timer);
    if (!this.#open) return;
    this.#triggerFor(this.#open)?.setAttribute('aria-expanded', 'false');
    this.#open.hidden = true;
    this.#open = null;
  }
}

new AgMegaMenu();

/**
 * Sticky header.
 *
 * Two things make this less trivial than `position: sticky` on the header.
 *
 * A sticky element only travels inside its own parent, and Horizon wraps every
 * header section in #header-group — so the group is what sticks, and a negative
 * `top` the height of the announcement bar slides that bar out of view while
 * the header row itself parks against the top. The bar is measured rather than
 * guessed, because its copy wraps to two lines on narrow screens.
 *
 * And from 990px up Horizon scrolls `.page-wrapper` instead of the window (it
 * needs an element scroller for the theme drawer), so the parked test reads
 * from whichever ancestor actually scrolls, and the listener captures scroll
 * events from any element rather than only the window.
 *
 * On a cover page, keep the expanded transparent row until the photo reaches
 * the bottom of the header. Otherwise the row compacts as soon as it parks.
 */
class AgStickyHeader {
  #frame = 0;
  #events = new AbortController();
  #observer;

  constructor() {
    this.header = /** @type {HTMLElement | null} */ (document.querySelector('.ag-header'));
    if (!this.header) return;
    this.section = this.header.closest('.shopify-section');
    this.group = this.section?.parentElement;
    if (!this.section || !this.group || this.group.id !== 'header-group') return;

    this.sticky = this.header.hasAttribute('data-ag-sticky');
    this.cover = document.querySelector('#MainContent > .shopify-section:first-child [data-ag-header-cover]');
    this.group.classList.toggle('ag-header-group--sticky', this.sticky);
    this.#measure();
    this.#update();

    const schedule = () => {
      if (this.#frame) return;
      this.#frame = requestAnimationFrame(() => {
        this.#frame = 0;
        this.#update();
      });
    };

    // Capture, so a scrolling `.page-wrapper` reaches us too — scroll events
    // from an element do not bubble.
    document.addEventListener('scroll', schedule, { capture: true, passive: true, signal: this.#events.signal });
    window.addEventListener('resize', () => {
      this.#measure();
      schedule();
    }, { signal: this.#events.signal });

    document.fonts.ready.then(() => {
      if (this.#events.signal.aborted) return;
      this.#measure();
      schedule();
    });

    // The announcement copy rewraps on rotation and on theme-editor edits.
    this.#observer = new ResizeObserver(() => {
      this.#measure();
      schedule();
    });
    this.#observer.observe(this.group);
  }

  /** Height of everything sitting above the header inside the group. */
  #measure() {
    const above = this.section.getBoundingClientRect().top - this.group.getBoundingClientRect().top;
    this.group.style.setProperty('--ag-annc-h', `${Math.max(0, Math.round(above))}px`);

    /* What the parked bar covers, published for anything else that sticks: the
       collection's filter bar parked at 0 and spent the whole scroll behind
       the header, categories and all. The row keeps its height in both states
       — the signet gives back exactly what the wordmark takes — so one
       measurement holds. */
    const covered = this.sticky ? Math.round(this.header.getBoundingClientRect().height) : 0;
    document.documentElement.style.setProperty('--ag-header-h', `${covered}px`);
    if (this.cover) {
      // Measure the expanded row, even when the mobile sticky nav is collapsed.
      // This offset and the cover threshold must not depend on the sticky state.
      this.header.classList.add('ag-header--measuring');
      this.coverHeaderHeight = this.header.getBoundingClientRect().height;
      document.documentElement.style.setProperty('--ag-cover-header-height', `${this.coverHeaderHeight}px`);
      this.header.classList.remove('ag-header--measuring');
    }
  }

  /** The ancestor that actually scrolls, or null when that is the window. */
  #scroller() {
    for (let node = this.group.parentElement; node; node = node.parentElement) {
      const overflow = getComputedStyle(node).overflowY;
      if (overflow === 'auto' || overflow === 'scroll') return node;
    }
    return null;
  }

  #update() {
    // Resolved every frame on purpose: under `html[scroll-lock]` (cart drawer,
    // search overlay) `.page-wrapper` is overflow:hidden and a cached answer
    // taken then would be null for the rest of the page's life.
    const scroller = this.#scroller();
    const ceiling = scroller?.getBoundingClientRect().top ?? 0;
    // Parked means the header has reached the top of whatever is scrolling.
    const stuck = this.sticky && (this.cover
      ? this.cover.getBoundingClientRect().bottom <= ceiling + this.coverHeaderHeight
      : this.header.getBoundingClientRect().top <= ceiling + 0.5);
    this.header.classList.toggle('ag-header--stuck', stuck);
    const scrollTop = scroller?.scrollTop ?? window.scrollY;
    this.header.classList.toggle('ag-header--cover-scrolled', !!this.cover && this.sticky && !stuck && scrollTop > 4);
  }

  destroy() {
    this.#events.abort();
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#frame);
  }
}

let stickyHeader = new AgStickyHeader();
// Header or first-section edits can change both the cover and sticky setting.
for (const event of ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder']) {
  document.addEventListener(event, () => {
    stickyHeader.destroy();
    stickyHeader = new AgStickyHeader();
  });
}
